/**
 * PATH: packages/ui/src/hooks/navOrder.ts
 * FILE_ID: UI-NAV-ORDER-001
 * PURPOSE: Pure I→O helpers that decide the final main-nav item order
 *          for a hub — manual Move-Up/Down order + the usage-driven
 *          "Smart ordering" engine.
 * WHY: Extracted from useNavCustomization.ts (which crossed the 500-line
 *      ceiling) so the ordering math is a small, side-effect-light,
 *      independently testable unit. The hook composes these; the
 *      settings UI and tests can import them directly.
 * FLOW:
 *   ┌──────────────┐   ┌────────────────────┐   ┌──────────────────┐
 *   │ config items │ → │ applyNavItemOrder  │ → │ resolveOrdered   │
 *   │ + user order │   │ (manual base)      │   │ NavItems (+usage)│
 *   └──────────────┘   └────────────────────┘   └──────────────────┘
 * DEPENDENCIES:
 *   - ../lib/navTracking#getOrderedNavItems: the usage-scoring engine.
 *   - ../components/types: NavItem / HubId shapes.
 * RELATED:
 *   - UI-NAV-HOOK-001 (useNavCustomization.ts): the consumer hook.
 */

import type { HubId, NavItem } from '../components/types'
import { getOrderedNavItems } from '../lib/navTracking'

/**
 * Compute the ordered nav-item list for a hub by applying the user's
 * Move-Up/Down preferences on top of the config-defined order.
 *
 * Algorithm:
 *   1. Take the user's preferred order (may reference items that no
 *      longer exist after a hub config change — those are dropped).
 *   2. Append config items not present in the preferred order, in
 *      their config-defined order (so new nav items added in a hub
 *      release just appear at the bottom for existing users).
 */
export function applyNavItemOrder(
  navItems: NavItem[],
  order: string[] | undefined,
): NavItem[] {
  if (!order || order.length === 0) return navItems
  const byId = new Map(navItems.map((n) => [n.id, n]))
  const out: NavItem[] = []
  const placed = new Set<string>()
  for (const id of order) {
    const item = byId.get(id)
    if (item) {
      out.push(item)
      placed.add(id)
    }
  }
  for (const item of navItems) {
    if (!placed.has(item.id)) out.push(item)
  }
  return out
}

/** Tuning for the flag-gated usage engine (see `resolveOrderedNavItems`). */
export interface ResolveOrderedNavOptions {
  /**
   * How many leading items stay anchored in place. Default 1 (anchors the
   * hub overview / Dashboard). Pass 0 to let the clicked item reach the
   * ABSOLUTE top (index 0) — the master rail's "click → top" structuring.
   */
  preserveFirst?: number
  /**
   * Ordering strategy for the unanchored tail. 'usage' (default) =
   * click-count weighted; 'recency' = most-recently-clicked floats to the
   * front. See navTracking#getOrderedNavItems.
   */
  strategy?: 'usage' | 'recency'
}

/**
 * Compose the two ordering layers into the final main-nav order:
 *
 *   1. `applyNavItemOrder` — the user's explicit Move-Up/Down manual
 *      order (right-click menu). This is the always-applied base.
 *   2. `getOrderedNavItems` — the usage-driven "Smart ordering" engine
 *      (navTracking.ts), applied ONLY when `autoOrderEnabled` is true.
 *      `options.strategy` picks usage-weighted (default) vs recency, and
 *      `options.preserveFirst` (default 1) anchors the leading item(s).
 *
 * Pure I→O: the only hidden input is the usage store the engine reads
 * from localStorage (`quotech_nav_usage`). When `autoOrderEnabled` is
 * false this is a strict passthrough of the manual order, so the gate
 * is a true no-op — zero behaviour change for users who haven't opted
 * in. Omitting `options` preserves the historical (preserveFirst:1,
 * usage) behaviour, so existing callers are untouched.
 */
export function resolveOrderedNavItems(
  navItems: NavItem[],
  manualOrder: string[] | undefined,
  hubId: string,
  autoOrderEnabled: boolean,
  options: ResolveOrderedNavOptions = {},
): NavItem[] {
  const manual = applyNavItemOrder(navItems, manualOrder)
  if (!autoOrderEnabled) return manual
  const { preserveFirst = 1, strategy = 'usage' } = options
  return getOrderedNavItems(manual, hubId as HubId, {
    enabled: true,
    preserveFirst,
    strategy,
  })
}

/**
 * Split an ordered nav list into the items the user still sees (`visible`)
 * and the ones they've hidden (`hidden`), preserving the input order within
 * each bucket. Pure I→O — never mutates the input.
 *
 *   partitionHidden([A,B,C], ['b']) → { visible: [A,C], hidden: [B] }
 *
 * An empty / undefined `hiddenIds` is a strict passthrough (everything
 * visible, nothing hidden) so callers who never hide anything pay nothing.
 */
export function partitionHidden(
  items: NavItem[],
  hiddenIds: readonly string[] | undefined,
): { visible: NavItem[]; hidden: NavItem[] } {
  if (!hiddenIds || hiddenIds.length === 0) return { visible: items, hidden: [] }
  const hiddenSet = new Set(hiddenIds)
  const visible: NavItem[] = []
  const hidden: NavItem[] = []
  for (const item of items) {
    if (hiddenSet.has(item.id)) hidden.push(item)
    else visible.push(item)
  }
  return { visible, hidden }
}

/**
 * Toggle an id in a hidden-list, returning a NEW array. Adding is
 * idempotent (no duplicates); removing a missing id is a no-op clone.
 */
export function toggleHidden(
  hiddenIds: readonly string[],
  itemId: string,
  hide: boolean,
): string[] {
  const has = hiddenIds.includes(itemId)
  if (hide) return has ? hiddenIds.slice() : [...hiddenIds, itemId]
  return has ? hiddenIds.filter((id) => id !== itemId) : hiddenIds.slice()
}

/**
 * Move an item up or down within an ordered list. Returns a NEW array
 * — the caller never has to remember to clone. No-ops gracefully when
 * the item is missing or already at the boundary.
 */
export function reorderItem(
  order: string[],
  itemId: string,
  direction: 'up' | 'down',
): string[] {
  const idx = order.indexOf(itemId)
  if (idx === -1) return order
  if (direction === 'up' && idx === 0) return order
  if (direction === 'down' && idx === order.length - 1) return order
  const next = order.slice()
  const swapIdx = direction === 'up' ? idx - 1 : idx + 1
  ;[next[idx], next[swapIdx]] = [next[swapIdx], next[idx]]
  return next
}
